<template>
  <div ref="root" class="filter-presets">
    <button class="presets-btn" type="button" @click.stop="toggleOpen">
      <span class="presets-label">Пресеты</span>
      <i class="fa-solid fa-chevron-down" :class="{ rotated: open }"></i>
    </button>
    <div v-if="open" class="presets-menu" @click.stop>
      <div class="menu-title">Мои пресеты</div>
      <div v-if="!items.length" class="presets-empty">Пока нет сохранённых наборов</div>
      <div v-for="item in items" :key="item.id" class="preset-row">
        <template v-if="renamingId === item.id">
          <input
            ref="renameInput"
            v-model="renameValue"
            class="preset-input"
            type="text"
            maxlength="60"
            @keyup.enter="commitRename(item)"
            @keyup.esc="cancelRename"
          />
          <button class="row-action ok" type="button" title="Сохранить имя" @click="commitRename(item)">✓</button>
          <button class="row-action" type="button" title="Отмена" @click="cancelRename">✕</button>
        </template>
        <template v-else>
          <button class="preset-apply" type="button" :title="`Применить «${item.name}»`" @click="applyItem(item)">
            {{ item.name }}
          </button>
          <button
            class="row-action"
            type="button"
            title="Обновить из текущих фильтров"
            @click="updateItem(item)"
          >⟳</button>
          <button class="row-action" type="button" title="Переименовать" @click="startRename(item)">✎</button>
          <button class="row-action danger" type="button" title="Удалить" @click="deleteItem(item)">✕</button>
        </template>
      </div>
      <div class="menu-divider"></div>
      <template v-if="saving">
        <div class="save-row">
          <input
            ref="saveInput"
            v-model="saveValue"
            class="preset-input"
            type="text"
            placeholder="Название пресета"
            maxlength="60"
            @keyup.enter="commitSave"
            @keyup.esc="cancelSave"
          />
          <button class="row-action ok" type="button" title="Сохранить" @click="commitSave">✓</button>
          <button class="row-action" type="button" title="Отмена" @click="cancelSave">✕</button>
        </div>
      </template>
      <button v-else class="preset-add" type="button" @click="startSave">+ Сохранить текущие</button>
    </div>
  </div>
</template>

<script>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

// Пользовательские пресеты фильтров: именованные снапшоты текущего набора
// фильтров в localStorage. Без встроенных пресетов — только create/rename/
// update/delete. Применение — кликом, без подтверждения.
function makeId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

export default {
  name: 'FilterPresetsDropdown',
  props: {
    storageKey: { type: String, required: true },
    current: { type: Object, default: null },
  },
  emits: ['apply'],
  setup(props, { emit }) {
    const open = ref(false);
    const root = ref(null);
    const items = ref([]);
    const saving = ref(false);
    const saveValue = ref('');
    const renamingId = ref(null);
    const renameValue = ref('');
    const saveInput = ref(null);
    const renameInput = ref(null);

    const loadItems = () => {
      try {
        const raw = window.localStorage.getItem(props.storageKey);
        const parsed = raw ? JSON.parse(raw) : null;
        items.value = Array.isArray(parsed?.items)
          ? parsed.items.filter(
              (it) => it && typeof it.id === 'string' && typeof it.name === 'string' && it.data && typeof it.data === 'object'
            )
          : [];
      } catch (e) {
        items.value = [];
      }
    };
    loadItems();

    const persist = () => {
      try {
        window.localStorage.setItem(props.storageKey, JSON.stringify({ items: items.value }));
      } catch (e) {
        /* localStorage может быть недоступен — молча игнорируем */
      }
    };

    const clone = (value) => JSON.parse(JSON.stringify(value));

    const toggleOpen = () => {
      open.value = !open.value;
      if (open.value) {
        cancelSave();
        cancelRename();
        loadItems();
      }
    };

    const applyItem = (item) => {
      emit('apply', clone(item.data));
      open.value = false;
    };

    const updateItem = (item) => {
      if (!props.current) return;
      item.data = clone(props.current);
      persist();
    };

    const deleteItem = (item) => {
      items.value = items.value.filter((it) => it.id !== item.id);
      persist();
    };

    const startRename = (item) => {
      renamingId.value = item.id;
      renameValue.value = item.name;
      nextTick(() => {
        // Внутри v-for template-ref — массив элементов (рендерится один инпут)
        const el = Array.isArray(renameInput.value)
          ? renameInput.value[0]
          : renameInput.value;
        if (el) el.focus();
      });
    };
    const cancelRename = () => {
      renamingId.value = null;
      renameValue.value = '';
    };
    const commitRename = (item) => {
      const name = renameValue.value.trim();
      if (name) {
        item.name = name.slice(0, 60);
        persist();
      }
      cancelRename();
    };

    const startSave = () => {
      saving.value = true;
      saveValue.value = '';
      nextTick(() => {
        if (saveInput.value) saveInput.value.focus();
      });
    };
    const cancelSave = () => {
      saving.value = false;
      saveValue.value = '';
    };
    const commitSave = () => {
      const name = saveValue.value.trim();
      if (name && props.current) {
        items.value.push({
          id: makeId(),
          name: name.slice(0, 60),
          createdAt: new Date().toISOString(),
          data: clone(props.current),
        });
        persist();
      }
      cancelSave();
    };

    const onDocClick = (event) => {
      if (root.value && !root.value.contains(event.target)) {
        open.value = false;
        cancelSave();
        cancelRename();
      }
    };
    const onKeydown = (event) => {
      if (event.key === 'Escape' && open.value) {
        open.value = false;
        cancelSave();
        cancelRename();
      }
    };
    onMounted(() => {
      document.addEventListener('click', onDocClick);
      document.addEventListener('keydown', onKeydown);
    });
    onBeforeUnmount(() => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKeydown);
    });

    return {
      open,
      root,
      items,
      saving,
      saveValue,
      renamingId,
      renameValue,
      saveInput,
      renameInput,
      toggleOpen,
      applyItem,
      updateItem,
      deleteItem,
      startRename,
      cancelRename,
      commitRename,
      startSave,
      cancelSave,
      commitSave,
    };
  },
};
</script>

<style scoped>
.filter-presets {
  position: relative;
}

.presets-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: #1d2939;
}

.presets-btn:hover {
  background: #f8fafc;
}

.presets-label {
  color: #667085;
}

.presets-btn i {
  font-size: 11px;
  color: #667085;
  transition: transform 0.15s;
}

.presets-btn i.rotated {
  transform: rotate(180deg);
}

.presets-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 30;
  width: 300px;
  max-height: 380px;
  overflow-y: auto;
  padding: 6px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
}

.menu-title {
  padding: 6px 10px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #667085;
}

.presets-empty {
  padding: 10px;
  font-size: 13px;
  color: #98a2b3;
}

.preset-row,
.save-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.preset-apply {
  flex: 1;
  min-width: 0;
  padding: 7px 8px;
  border: none;
  border-radius: 6px;
  background: none;
  text-align: left;
  font-size: 13px;
  color: #1d2939;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preset-apply:hover {
  background: #eff6ff;
  color: #007bff;
}

.row-action {
  flex: none;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  background: none;
  font-size: 12px;
  color: #667085;
  cursor: pointer;
}

.row-action:hover {
  background: #f2f4f7;
  color: #1d2939;
}

.row-action.ok:hover {
  background: #eff8ef;
  color: #12b76a;
}

.row-action.danger:hover {
  background: #fff3f2;
  color: #d92d20;
}

.preset-input {
  flex: 1;
  min-width: 0;
  height: 28px;
  padding: 0 8px;
  border: 1px solid #84adff;
  border-radius: 6px;
  font-size: 13px;
  color: #101828;
  box-sizing: border-box;
}

.preset-input:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(23, 92, 211, 0.12);
}

.menu-divider {
  height: 1px;
  margin: 6px 4px;
  background: #e5e7eb;
}

.preset-add {
  display: block;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: none;
  text-align: left;
  font-size: 13px;
  color: #175cd3;
  cursor: pointer;
}

.preset-add:hover {
  background: #eff6ff;
}
</style>
